// src/cockpit4/components/RiskDetailPanel.tsx
// ============================================
// 风险维度下钻详情面板（P0-功能3 配套）
//
// 功能：
//   1. 展示该维度的健康度评分、风险等级、关键指标
//   2. 列出具体风险项
//   3. 一键跳转到对应工作区处理
//   4. 展示该维度的指标趋势（基于历史数据，如有）
//
// 触发：RiskRadarChart 中点击维度卡片
// ============================================

import { motion } from "framer-motion";
import {
  X,
  AlertTriangle,
  ChevronRight,
  TrendingDown,
  Activity,
  Target,
  ArrowUpRight,
} from "lucide-react";
import type { RiskDimension, RiskLevel } from "./RiskRadarChart";

const LEVEL_CONFIG: Record<RiskLevel, { label: string; color: string; bgColor: string }> = {
  normal: { label: "正常", color: "#10B981", bgColor: "rgba(16,185,129,0.15)" },
  concern: { label: "关注", color: "#F59E0B", bgColor: "rgba(245,158,11,0.15)" },
  warning: { label: "警告", color: "#F97316", bgColor: "rgba(249,115,22,0.15)" },
  critical: { label: "危险", color: "#EF4444", bgColor: "rgba(239,68,68,0.15)" },
};

// 维度 → 工作区跳转映射
const DIMENSION_WORKSPACE: Record<string, string> = {
  finance: "finance",
  customer: "crm",
  operations: "ceo",
  market: "marketing",
  talent: "knowledge",
};

interface RiskDetailPanelProps {
  /** 当前展示的风险维度 */
  dimension: RiskDimension;
  /** 关闭面板回调 */
  onClose: () => void;
  /** 跳转到工作区 */
  onJumpToWorkspace: (ws: string) => void;
}

export default function RiskDetailPanel({
  dimension,
  onClose,
  onJumpToWorkspace,
}: RiskDetailPanelProps) {
  const lvlCfg = LEVEL_CONFIG[dimension.level];
  const Icon = dimension.icon;
  const targetWorkspace = DIMENSION_WORKSPACE[dimension.id] || "ceo";

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.25 }}
      className="border-t border-white/[0.06] bg-white/[0.01] overflow-hidden"
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{
                background: `${dimension.color}15`,
                border: `1px solid ${dimension.color}30`,
              }}
            >
              <Icon size={14} style={{ color: dimension.color }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-[13px] font-semibold text-white tracking-tight">
                  {dimension.label}
                </h4>
                <span
                  className="px-1.5 py-0.5 rounded text-[9px] font-mono uppercase"
                  style={{ background: lvlCfg.bgColor, color: lvlCfg.color }}
                >
                  {lvlCfg.label}
                </span>
              </div>
              <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
                {dimension.labelEn} · 健康度 {dimension.score}/100
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-md flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
          >
            <X size={12} className="text-gray-400" />
          </button>
        </div>

        <div className="grid grid-cols-12 gap-3">
          {/* 左侧：关键指标 */}
          <div className="col-span-7">
            <div className="flex items-center gap-1.5 mb-2">
              <Target size={10} className="text-gray-500" />
              <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                关键指标
              </span>
            </div>
            <div className="space-y-1">
              {dimension.indicators.map((ind, i) => {
                const indCfg = LEVEL_CONFIG[ind.status];
                return (
                  <div
                    key={i}
                    className="flex items-center gap-2 p-2 rounded-md border bg-white/[0.02]"
                    style={{ borderColor: `${indCfg.color}25` }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ background: indCfg.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] text-gray-200 truncate">{ind.name}</span>
                        <span
                          className="text-[11px] font-mono font-bold"
                          style={{ color: indCfg.color }}
                        >
                          {ind.value}
                          {ind.unit && (
                            <span className="text-[9px] text-gray-500 ml-0.5">{ind.unit}</span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-[9px] font-mono text-gray-600 mt-0.5">
                        {ind.target !== undefined && (
                          <span>目标：{ind.target}{ind.unit || ""}</span>
                        )}
                        {ind.note && <span>· {ind.note}</span>}
                        <span>· {indCfg.label}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 右侧：风险项 + 操作 */}
          <div className="col-span-5">
            <div className="flex items-center gap-1.5 mb-2">
              <AlertTriangle size={10} className="text-gray-500" />
              <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                风险项（{dimension.riskItems.length}）
              </span>
            </div>
            {dimension.riskItems.length === 0 ? (
              <div className="p-3 rounded-md border border-emerald-500/20 bg-emerald-500/[0.03] text-center">
                <Activity size={14} className="mx-auto text-emerald-400 mb-1" />
                <p className="text-[10px] font-mono text-emerald-300">
                  暂无风险项
                </p>
                <p className="text-[9px] font-mono text-gray-600 mt-0.5">
                  该维度处于健康状态
                </p>
              </div>
            ) : (
              <div className="space-y-1 max-h-[180px] overflow-y-auto pr-1">
                {dimension.riskItems.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-1.5 p-1.5 rounded border border-white/[0.04] bg-white/[0.02]"
                  >
                    <TrendingDown
                      size={10}
                      className="text-amber-400 flex-shrink-0 mt-0.5"
                    />
                    <span className="text-[10px] text-gray-300 leading-snug flex-1">
                      {r}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* 一键跳转 */}
            <button
              onClick={() => onJumpToWorkspace(targetWorkspace)}
              className="w-full mt-3 flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
              title={`跳转到${dimension.label}相关模块处理`}
            >
              <ArrowUpRight size={11} className="text-cyan-400" />
              <span className="text-[10px] font-mono text-cyan-300 uppercase tracking-wider">
                前往处理
              </span>
              <ChevronRight size={10} className="text-cyan-400" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
